import { useId, useRef, useState } from 'react';

import { Button } from '@/components/Button/Button';
import { Card } from '@/components/Card/Card';

import { RemoveStudentDialog, type RemoveStudentTarget } from './RemoveStudentDialog';
import {
  formatSupportCalendarDate,
  formatSupportDate,
  formatSupportDateTime,
  pluralize,
  SUPPORT_ROLE_LABELS,
  supportErrorMessage,
} from './supportConsoleFormat';
import {
  supportDisplayName,
  type SupportCourseMembership,
  type SupportUserDetail,
} from './supportConsoleTypes';
import styles from './SupportUserPanel.module.css';
import { useRemoveStudent, useSupportUser } from './useSupportConsole';

interface SupportUserPanelProps {
  // Says what just happened, in the page's live region.
  onAnnounce(message: string): void;
  userId: string;
}

const NOT_SET = 'Not set';

function profileRows(user: SupportUserDetail): Array<[label: string, value: string]> {
  return [
    ['User ID', user.id],
    ['First name', user.firstName || NOT_SET],
    ['Last name', user.lastName || NOT_SET],
    ['Profile email', user.preferredEmail || NOT_SET],
    ['Username', user.preferredUsername || NOT_SET],
    ['Institution', user.institute || NOT_SET],
    ['Institution ID', user.instituteOfficialId || NOT_SET],
    ['Account type', user.isInstructor ? 'Instructor' : 'Student'],
    ['Signs in with', user.isGoogle ? 'Google' : 'Email and password'],
    ['Last sign-in on record', formatSupportDateTime(user.lastLoginAt)],
  ];
}

function courseName(course: SupportCourseMembership): string {
  return course.name || course.courseId;
}

// When the course runs, from its start and end dates.
function courseRuns(course: SupportCourseMembership): string {
  const start = formatSupportCalendarDate(course.startDate);
  const end = formatSupportCalendarDate(course.endDate);

  if (start && end) {
    return `Runs ${start} to ${end}`;
  }
  if (start) {
    return `Starts ${start}`;
  }

  return end ? `Ends ${end}` : 'No course dates';
}

function courseStatus(course: SupportCourseMembership): string {
  if (!course.exists) {
    return 'No course record';
  }
  if (course.isComplete) {
    return 'Complete';
  }

  return course.isLive ? 'Live' : 'Not live';
}

function coursePeople(course: SupportCourseMembership): string {
  const parts = [pluralize(course.studentCount, 'student', 'students')];

  if (course.secondaryInstructorCount > 0) {
    parts.push(
      pluralize(course.secondaryInstructorCount, 'additional instructor', 'additional instructors'),
    );
  }
  if (course.assistantCount > 0) {
    parts.push(pluralize(course.assistantCount, 'assistant', 'assistants'));
  }

  return parts.join(', ');
}

// Only someone the course does not name as staff can be removed from here.
// A role this page could not read is not a student.
function canRemove(course: SupportCourseMembership): boolean {
  return course.role === 'student';
}

// One user: their profile, and every course on their record.
export function SupportUserPanel({ onAnnounce, userId }: SupportUserPanelProps) {
  const headingId = useId();
  const captionId = useId();
  const record = useSupportUser(userId);
  const removeStudent = useRemoveStudent();
  const [target, setTarget] = useState<RemoveStudentTarget | null>(null);
  const captionRef = useRef<HTMLTableCaptionElement>(null);
  const emptyRef = useRef<HTMLParagraphElement>(null);

  if (record.isPending) {
    return (
      <Card className={styles.panel}>
        <p className={styles.note} role="status">
          Loading user…
        </p>
      </Card>
    );
  }

  if (!record.data) {
    return (
      <Card className={styles.panel}>
        <div className={styles.failure} role="alert">
          <p>{supportErrorMessage(record.error)}</p>
          <Button onClick={() => void record.refetch()} variant="secondary">
            Try again
          </Button>
        </div>
      </Card>
    );
  }

  const { courses, user } = record.data;
  const userName = supportDisplayName(user);

  return (
    <Card aria-labelledby={headingId} as="section" className={styles.panel}>
      <h2 className={styles.heading} id={headingId}>
        {userName}
      </h2>

      {/* The record was loaded once and could not be loaded again. It stays on
          screen, so that a pop-up over it is not torn away, with a warning. */}
      {record.isError ? (
        <div className={styles.failure} role="alert">
          <p>
            Could not load this user again, so what is shown may be out of date.{' '}
            {supportErrorMessage(record.error)}
          </p>
          <Button onClick={() => void record.refetch()} variant="secondary">
            Try again
          </Button>
        </div>
      ) : null}

      <dl className={styles.profile}>
        {profileRows(user).map(([label, value]) => (
          <div className={styles.profileRow} key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      <p className={styles.note}>
        Everything but the user ID is what this user typed into their own profile. Go by
        the ID when it matters who this is.
      </p>

      {courses.length === 0 ? (
        <p className={styles.note} ref={emptyRef} tabIndex={-1}>
          There are no courses on this user&apos;s record.
        </p>
      ) : (
        // A wide table in its own scrolling region, reachable by keyboard.
        <div
          aria-labelledby={captionId}
          className={styles.tableRegion}
          role="region"
          tabIndex={0}
        >
          <table className={styles.table}>
            <caption id={captionId} ref={captionRef} tabIndex={-1}>
              Courses on this user&apos;s record
            </caption>
            <thead>
              <tr>
                <th scope="col">Course</th>
                <th scope="col">Their role</th>
                <th scope="col">Main instructor</th>
                <th scope="col">Dates</th>
                <th scope="col">Status</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {courses.map((course) => (
                <tr key={course.courseId}>
                  <th scope="row">
                    <span className={styles.primary}>
                      {course.exists ? courseName(course) : 'No course record'}
                    </span>
                    <span className={styles.secondary}>ID {course.courseId}</span>
                  </th>
                  <td>
                    <span className={styles.primary}>{SUPPORT_ROLE_LABELS[course.role]}</span>
                    <span className={styles.secondary}>
                      {course.archived ? 'On their archived list' : 'On their course list'}
                    </span>
                    <span className={styles.secondary}>
                      {course.inRoster ? 'In the roster' : 'Not in the roster'}
                    </span>
                  </td>
                  <td>
                    {course.instructor ? (
                      <>
                        <span className={styles.primary}>
                          {supportDisplayName({
                            ...course.instructor,
                            preferredEmail: course.instructor.email,
                          })}
                        </span>
                        <span className={styles.secondary}>
                          {course.instructor.email || `ID ${course.instructor.id}`}
                        </span>
                      </>
                    ) : (
                      'Unknown'
                    )}
                  </td>
                  <td>
                    {course.exists ? (
                      <>
                        <span className={styles.primary}>
                          Created {formatSupportDate(course.createdAt)}
                        </span>
                        <span className={styles.primary}>
                          Updated {formatSupportDate(course.lastUpdatedAt)}
                        </span>
                        <span className={styles.secondary}>{courseRuns(course)}</span>
                      </>
                    ) : (
                      'Unknown'
                    )}
                  </td>
                  <td>
                    <span className={styles.primary}>{courseStatus(course)}</span>
                    <span className={styles.secondary}>{coursePeople(course)}</span>
                  </td>
                  <td>
                    {canRemove(course) ? (
                      <Button
                        aria-label={`Remove ${userName} from ${courseName(course)}`}
                        className={styles.removeButton}
                        destructive
                        onClick={() =>
                          setTarget({
                            courseId: course.courseId,
                            courseName: courseName(course),
                            userName,
                          })
                        }
                        variant="secondary"
                      >
                        Remove
                      </Button>
                    ) : (
                      <span className={styles.secondary}>
                        {course.role === 'unknown'
                          ? 'Not available'
                          : 'Staff are removed in the course itself'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RemoveStudentDialog
        focusAfterRemoval={() => (captionRef.current ?? emptyRef.current)?.focus()}
        onClose={() => setTarget(null)}
        onRemove={async (removal) => {
          const result = await removeStudent.mutateAsync({
            courseId: removal.courseId,
            userId: user.id,
          });
          const changed =
            result.removedFromCourseList ||
            result.removedFromArchivedList ||
            result.removedFromRoster;

          onAnnounce(
            changed
              ? `${removal.userName} was removed from ${removal.courseName}. Their saved work is kept.`
              : `${removal.userName} was already out of ${removal.courseName}. Nothing changed.`,
          );
        }}
        onRemoved={() => setTarget(null)}
        target={target}
      />
    </Card>
  );
}
